import { Button, Text, mergeClasses } from "@fluentui/react-components";
import {
  useUiStyles,
  IconTile,
  Footer,
  SparkleIcon,
  BoltIcon,
  ShieldIcon,
  CalendarIcon,
  DocumentIcon,
} from "./ui";
import { useLanguage } from "../i18n/LanguageContext";

interface SignInCardProps {
  onSignIn: () => void;
  error: string | null;
}

const FEATURE_ICONS = [<SparkleIcon key="0" />, <BoltIcon key="1" />, <CalendarIcon key="2" />];

/**
 * Landing + sign-in screen. Shown when the user is not authenticated (and in a
 * plain browser outside Teams). Rich enough to explain the product instead of a
 * single empty card.
 */
export function SignInCard({ onSignIn, error }: SignInCardProps) {
  const s = useUiStyles();
  const { t, tRaw } = useLanguage();
  const features = tRaw<{ title: string; desc: string }[]>("signIn.features");
  const steps = tRaw<string[]>("signIn.steps");

  return (
    <div className={s.landingWrap}>
      {/* Hero */}
      <div
        className={mergeClasses(s.card, s.accentTop)}
        style={{ padding: "30px 26px", textAlign: "center", marginBottom: "16px" }}
      >
        <div className={s.brandMark}>
          <SparkleIcon />
        </div>
        <Text size={700} weight="bold" block style={{ marginTop: "14px" }}>
          {t("signIn.brand")}
        </Text>
        <Text
          size={300}
          block
          style={{ margin: "8px auto 0", maxWidth: 380, color: "var(--colorNeutralForeground2)" }}
        >
          {t("signIn.tagline")}
        </Text>

        <Button
          appearance="primary"
          size="large"
          onClick={onSignIn}
          style={{ width: "100%", maxWidth: 320, marginTop: "20px" }}
        >
          {t("signIn.button")}
        </Button>

        <div
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: "6px",
            marginTop: "12px",
            color: "var(--colorNeutralForeground3)",
            fontSize: "12px",
          }}
        >
          <ShieldIcon />
          <span>{t("signIn.privacyNote")}</span>
        </div>

        {error && (
          <Text block style={{ marginTop: "14px", color: "var(--colorPaletteRedForeground1)" }}>
            {error}
          </Text>
        )}
      </div>

      {/* Feature highlights */}
      <div className={s.card} style={{ padding: "8px 18px", marginBottom: "16px" }}>
        {features.map((f, i) => (
          <div key={f.title}>
            {i > 0 && <div className={s.divider} />}
            <div className={s.featureRow}>
              <IconTile variant="brand">{FEATURE_ICONS[i]}</IconTile>
              <div className={s.grow}>
                <Text weight="semibold" block>
                  {f.title}
                </Text>
                <Text size={200} block style={{ color: "var(--colorNeutralForeground3)", marginTop: "2px" }}>
                  {f.desc}
                </Text>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* How it works */}
      <div className={mergeClasses(s.card, s.accentLeft)} style={{ padding: "16px 18px" }}>
        <div className={s.row} style={{ alignItems: "center", marginBottom: "10px" }}>
          <IconTile variant="brand">
            <DocumentIcon />
          </IconTile>
          <Text weight="semibold">{t("signIn.howItWorks")}</Text>
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
          {steps.map((step, i) => (
            <div key={i} style={{ display: "flex", alignItems: "center", gap: "10px" }}>
              <span className={s.stepNum}>{i + 1}</span>
              <Text size={200} style={{ color: "var(--colorNeutralForeground2)" }}>
                {step}
              </Text>
            </div>
          ))}
        </div>
      </div>

      <Footer>
        {t("signIn.footer")}
      </Footer>
    </div>
  );
}
